import React, { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import "./checklistTable.css";

interface CaseActionItemsProps {
  assignmentID?: string;
  onSuccess?: () => void;
}

const getCheckFamily = (name?: string): string =>
  (name || "")
    .trim()
    .toLowerCase()
    .split(" - ")[0]
    .replace("employement", "employment")
    .trim();

const CheckListTable: React.FC<CaseActionItemsProps> = ({
  assignmentID,
  onSuccess,
}) => {
  const [allChecksList, setAllChecksList] = useState<any[]>([]);
  const [checkName, setCheckName] = useState<any[]>([]);

  const getChecksHistoryAPI = useApi<any>(
    assignmentID
      ? `/api/v1/candidate/forms/case-check-summary/${assignmentID}`
      : "",
    {
      method: "GET",
      autoCall: true,
      handleGlobalError: false,
    },
  );

  const getCases = useApi<any>(
    assignmentID ? `/api/v1/admin/checks-details/${assignmentID}` : "",
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const submitChecksApi = useApi<any>(webservice_url.addMoreChecksAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  useEffect(() => {
    const fetchCheckHistory = async () => {
      try {
        const res = await getChecksHistoryAPI.callApi();
        if (res.success) {
          setCheckName(res?.data?.checks);
        }
      } catch (err: any) {
        console.error(err);
      }
    };

    fetchCheckHistory();
  }, [assignmentID]);

  const fetchCasesHistory = async () => {
    if (!assignmentID) return;

    try {
      const res = await getCases.callApi();

      const formattedData =
        res?.data?.map((group: any) => ({
          category: group.header_name,
          checks: group.checks.map((check: any) => ({
            check_id: check.check_id,
            label: check.check_name,
            value: check.check_id,
            description: check.check_desc,
            price: check.price,
            status: check.status,
          })),
        })) || [];

      setAllChecksList(formattedData);
    } catch (error) {
      console.error(error);
      setAllChecksList([]);
    }
  };

  useEffect(() => {
    if (assignmentID) {
      fetchCasesHistory();
    }
  }, [assignmentID]);

  const toggleCheck = useCallback((checkId: string) => {
    setAllChecksList((prev) =>
      prev.map((category) => ({
        ...category,
        checks: category.checks.map((check: any) =>
          check.check_id === checkId
            ? {
                ...check,
                selected: !check.selected,
              }
            : check,
        ),
      })),
    );
  }, []);

  const selectedCheckList = useMemo(
    () =>
      allChecksList.flatMap((cat) => cat.checks.filter((c: any) => c.selected)),
    [allChecksList],
  );

  const handleSubmitOrder = async () => {
    if (!assignmentID) return; 
    const checkNames = selectedCheckList
      .map((item: any) => item.label)
      .join(", ");

    const confirm = await Swal.fire({
      icon: "question",
      title: "Add Checks?",
      text: `You are about to add ${selectedCheckList.length} check${
        selectedCheckList.length > 1 ? "s" : ""
      }: ${checkNames}. Do you want to continue?`,
      showCancelButton: true,
      confirmButtonText: "Yes, add",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;
    const payload = {
      assignment_id: assignmentID,
      check_ids: selectedCheckList.map((item: any) => item.check_id),
    };

    try {
      const res = await submitChecksApi.callApi(payload);
      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Checks Added Successfully",
          text:
            res?.message || "The selected checks have been added successfully.",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });

        await fetchCasesHistory();
        await onSuccess?.();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Unable to Add Checks",
          text:
            res?.message ||
            "Something went wrong while adding the selected checks.",
          confirmButtonText: "OK",
        });
      }
    } catch (err: any) {
      console.error(err);

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Unable to submit order. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };
 
  const existingCheckFamilies = useMemo(
    () =>
      new Set(
        (Array.isArray(checkName) ? checkName : [])
          .map((item: any) => getCheckFamily(item?.check_name))
          .filter(Boolean),
      ),
    [checkName],
  );

  const loader = getCases.loading;

  return (
    <>
      {loader ? (
        <SnipperLoader />
      ) : (
        <div className="card clt-card mt-4">
          <div className="clt-header d-flex align-items-center px-3 py-3">
            <span className="clt-step-badge">3</span>
            <span className="fw-semibold text-dark">
              Select the checks and quantity
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered mb-0 clt-table">
              <tbody>
                {allChecksList?.map((group) => (
                  <React.Fragment key={group.category}>
                    <tr className="clt-group-row">
                      <th className="bg-dark text-white text-uppercase">
                        {group.category}
                      </th> 
                    </tr>

                    {group.checks.map((check: any) => { 
                      const isDisabled = existingCheckFamilies.has(
                        getCheckFamily(check?.label),
                      );

                      return (
                        <tr key={check.check_id} className="clt-check-row">
                          <td>
                            <div className="py-1 d-flex align-items-center">
                              <input
                                className="form-check-input clt-form-check-input"
                                type="checkbox"
                                id={check.check_id}
                                checked={!!check.selected}
                                disabled={isDisabled}
                                onChange={() => toggleCheck(check.check_id)}
                              />

                              <label
                                className="form-check-label clt-check-label ms-2 mb-0"
                                htmlFor={check.check_id}
                                title={
                                  isDisabled
                                    ? "This type of check is already added"
                                    : undefined
                                }
                                style={{
                                  opacity: isDisabled ? 0.6 : 1,
                                  cursor: isDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                              >
                                {check.label}
                              </label>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card clt-card mt-4 pb-4">
        <div className="clt-header d-flex align-items-center px-3 py-3">
          <span className="clt-step-badge">4</span>
          <span className="fw-semibold text-dark">
            You have added these checks
          </span>
        </div>

        <ul className="clt-summary-list py-2 px-3 mb-0">
          {selectedCheckList?.length ? (
            selectedCheckList.map((check, idx) => (
              <li
                key={check.check_id}
                className="clt-summary-item py-2 px-2 d-flex align-items-center justify-content-between"
                style={{ maxWidth: "600px" }}
              >
                <div className="d-flex align-items-center">
                  <span className="clt-summary-index">{idx + 1}</span>
                  <span className="text-dark">{check.label}</span>
                </div> 
              </li>
            ))
          ) : (
            <li className="py-2 px-2 clt-empty-state">
              No checks selected yet.
            </li>
          )} 
        </ul>

        <div className="d-flex justify-content-end align-items-center gap-2 px-3 mt-4">
          <button
            className="btn btn-primary ac-add-check-btn d-inline-flex align-items-center"
            onClick={handleSubmitOrder}
            disabled={!selectedCheckList.length || submitChecksApi.loading}
          >
            {submitChecksApi.loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Processing...
              </>
            ) : (
              "Add more checks"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default CheckListTable; 